// eslint-disable-next-line import/no-anonymous-default-export
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { CheckBox } from 'devextreme-react';
import moment from 'moment-timezone';
import {
  Chart,
  CommonSeriesSettings,
  Legend,
  SeriesTemplate,
  ArgumentAxis,
  ValueAxis,
  Tick,
  Size,
} from 'devextreme-react/chart';
import { CRS, latLngBounds } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapContainer, ImageOverlay, Polygon, useMap } from 'react-leaflet';
import SpacesService from 'api/spaces.service';
import AssetsService from '../../../api/assets.service';

const UtilizationHeatMap = (props) => {
  const { assetIds, startDate, endDate, isActive } = props;
  console.log('in utilization', assetIds);

  // 1. FINGERPRINT GUARD: This is the most important part.
  // It prevents the effect from running if the values haven't actually changed.
  const syncLock = useRef('');

  const [spaces, setSpaces] = useState([]);
  const [motionBarChart, setMotionBarChart] = useState([]);
  const [bounds, setBounds] = useState(new latLngBounds([0, 0], [25, 35]));
  const [heatmapData, setHeatMapData] = useState([]);
  const [floorplanHeight, setFloorplanHeight] = useState(0);
  const [floorplanWidth, setFloorplanWidth] = useState(0);
  const [floorplan, setFloorplan] = useState('');
  const [isMotion, setIsMotion] = useState(true);
  const [isStationary, setIsStationary] = useState(true);
  const [utilization, setUtilization] = useState(0);
  const [timePeriod, setTimePeriod] = useState(0);
  const [totalDistance, setTotalDistance] = useState(0);

  // Helper: Utilization Calculation
  const calculateUtilizationInternal = (data) => {
    let totalMs = 0;
    data.forEach((item) => {
      if (item.action === 'Motion' && item.start && item.end) {
        const start = new Date(item.start);
        const end = new Date(item.end);
        const diff = end - start;
        if (diff > 0) totalMs += diff;
      }
    });
    return totalMs / (1000 * 60); // minutes
  };

  useEffect(() => {
    console.log("in use effect")
    // 2. CREATE A UNIQUE FINGERPRINT
    const currentFingerprint = JSON.stringify([
      assetIds,
      startDate,
      endDate,
      isMotion,
      isStationary,
      isActive,
    ]);

    // 3. THE "KILL SWITCH"
    // If we are not active, or if we just ran this exact data request, STOP.
    if (!isActive) {
      console.log("1")
      syncLock.current = ""; // Reset lock when hidden so it can re-run when user returns
      return;
    }
    
    if (syncLock.current === currentFingerprint) {
      console.log("2")
      return;
    }

    const loadDashboard = async () => {
      console.log("3")
      syncLock.current = currentFingerprint;
    
      const from = startDate;
      const to = endDate;
    
      // Initial variables for scope
      let processedChartData = [];
      let calcUtilization = 0;
      let calcTimePeriod = 0;
      let distance = 0;
      let spaceMapData = [];
      let floorplansList = [];
    
      if (!from || from === 'Invalid date' || !to || to === 'Invalid date' || !Array.isArray(assetIds)) {
        console.log("4", !assetIds)
        return;
      }
    
      // Reset state upfront so stale data is cleared immediately
      setSpaces([]);
      setHeatMapData([]);
      setMotionBarChart([]);
      setUtilization(0);
      setTimePeriod(0);
      setTotalDistance(0);
      setFloorplan((prev) => {
        if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
        return '';
      });
    
      try {
        console.log("5", assetIds)
        const initialHistoryRes = await AssetsService.locationHistoryMultiple(
          assetIds,
          from,
          to,
          null,
          'asset'
        );
        floorplansList = initialHistoryRes?.data?.data?.floorplans || [];
        console.log('floorplansList', floorplansList);
    
        // Always update spaces, even if empty
        setSpaces(floorplansList);


    
        if (floorplansList.length > 0) {
          const primarySpaceId = floorplansList[0].id;
          console.log("assetIds!",assetIds)
    
          // Parallel Fetch
          const [chartRes, heatmapRes, spaceRes, floorplanBlobRes, detailedHistoryRes] =
            await Promise.all([
              AssetsService.getMultipleAssetUtilizationChart(
                assetIds,
                primarySpaceId,
                from,
                to,
                isMotion,
                isStationary
              ),
              AssetsService.getMultipleAssetUtilizationHeatMap(
                assetIds,
                primarySpaceId,
                from,
                to,
                isMotion,
                isStationary
              ),
              SpacesService.getSpace(primarySpaceId),
              SpacesService.getSpaceFloorplan(primarySpaceId),
              AssetsService.locationHistoryMultiple(assetIds, from, to, primarySpaceId),
            ]);
            console.log("chartRes, heatmapRes, spaceRes, floorplanBlobRes, detailedHistoryRes",chartRes, heatmapRes, spaceRes, floorplanBlobRes, detailedHistoryRes)
    
          // Process floorplan blob
          if (floorplanBlobRes?.data) {
            const imageUrl = URL.createObjectURL(floorplanBlobRes.data);
            setFloorplan(imageUrl);
          }
    
          // Process space coordinates
          if (spaceRes?.data?.data?.localCoordinates) {
            const coords = spaceRes.data.data.localCoordinates;
            let xMax = 0;
            let yMax = 0;
            coords.forEach((c) => {
              if (c[1] > xMax) xMax = c[1];
              if (c[0] > yMax) yMax = c[0];
            });
            setFloorplanWidth(xMax);
            setFloorplanHeight(yMax);
            setBounds(new latLngBounds([0, 0], [xMax, yMax]));
          }
    
          // Process chart data
          if (chartRes.data.status === 200 && chartRes.data.data?.timeGraphData) {
            processedChartData = chartRes.data.data.timeGraphData.map((item) => ({
              ...item,
              start: new Date(item.start),
              end: new Date(item.end),
              activity: 'Device Motion',
            }));
            calcUtilization = calculateUtilizationInternal(processedChartData);
          }
    
          calcTimePeriod = (new Date(to) - new Date(from)) / (1000 * 60);
          distance = detailedHistoryRes?.data?.data?.totalDistance || 0;
          spaceMapData = heatmapRes.data.status === 200 ? heatmapRes.data.data.mapData : [];
    
          // Atomic state update
          setHeatMapData(spaceMapData);
          setTotalDistance(distance);
          setMotionBarChart(processedChartData);
          setUtilization(calcUtilization);
          setTimePeriod(calcTimePeriod);
        }
      } catch (error) {
        console.error('Dashboard Load Failed:', error);
        setSpaces([]);
        setHeatMapData([]);
        setMotionBarChart([]);
        setUtilization(0);
        setTimePeriod(0);
        setTotalDistance(0);
      }
    };

    loadDashboard();

    return () => {
      // Cleanup Blob URLs to prevent memory leaks
      setFloorplan((prev) => {
        if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
        return '';
      });
    };
  }, [startDate, endDate, assetIds, isStationary, isMotion, isActive]);

  // UI Helper functions
  const customizeSeries = (series) => {
    return series === 'Motion' ? { color: '#FFC001' } : { color: '#CCCCCC' };
  };

  const customizeTextLegend = (item) => {
    return item.seriesName === 'noMotion' ? 'No Utilization' : 'Utilization';
  };

  const bands = [
    { max: 1 / 60, color: '#BA8DC1' },
    { max: 5 / 60, color: '#99D5FF' },
    { max: 15 / 60, color: '#79DE85' },
    { max: 1, color: '#FFFF00' },
    { max: 6, color: '#FEB800' },
    { max: 24, color: '#FF0000' },
  ];

  function getColorFromBand(hoursPerDay) {
    for (let band of bands) {
      if (hoursPerDay <= band.max) return band.color;
    }
    return 'red';
  }

  const normalizedGrid = useMemo(() => {
    if (!Array.isArray(heatmapData) || heatmapData.length === 0) return [];
    console.log("heatmapData",heatmapData)
    const totalDays = (new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24) || 1;
    const grid = {};
    heatmapData.forEach(({ lat, lng, value }) => {
      const x = Math.floor(lng);
      const y = Math.floor(lat);
      const key = `${x},${y}`;
      grid[key] = (grid[key] || 0) + value;
    });

    return Object.entries(grid).map(([key, totalSeconds]) => {
      const [x, y] = key.split(',').map(Number);
      const hoursPerDay = totalSeconds / 3600 / totalDays;
      return { x, y, color: getColorFromBand(hoursPerDay) };
    });
  }, [heatmapData, startDate, endDate]);

  const legendBoxStyle = (bgColor) => ({
    backgroundColor: bgColor,
    padding: '6px 10px',
    width: '100px',
    color: '#000',
    fontSize: '12px',
    textAlign: 'center',
    borderRadius: '4px',
  });

  const onMotionChangeInternal = (type) => {
    if (type === 'isMotion') setIsMotion(!isMotion);
    if (type === 'isStationary') setIsStationary(!isStationary);
  };

  // Sub-component to handle automatic zooming
  const MapAutoFit = ({ bounds }) => {
    const map = useMap();
    useEffect(() => {
      if (bounds) {
        map.fitBounds(bounds, { animate: true, padding: [10, 10] });
      }
    }, [bounds, map]);
    return null;
  };

  const formatTime = (totalMinutes) => {
    const roundedMinutes = Math.round(totalMinutes);

    const days = Math.floor(roundedMinutes / 1440);
    const hours = Math.floor((roundedMinutes % 1440) / 60);
    const minutes = roundedMinutes % 60;

    const parts = [];
    if (days > 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
    if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
    if (minutes > 0 || parts.length === 0)
      parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);

    return parts.join(', ');
  };

  return (
    <div className="utilization-container">
      <div className="utilizationTextCheckbox">
        <p>
          Asset Utilization is
          <b> {timePeriod > 0 ? ((utilization / timePeriod) * 100).toFixed(3) : 0}% </b>
          or
          <b> {formatTime(utilization)} </b>
          over
          <b> {formatTime(timePeriod)} </b>
          and it travelled
          <b> {totalDistance} meters</b>
        </p>
      </div>

      <div style={{ width: '100%', marginTop: '15px', marginBottom: '15px' }}>
        {motionBarChart.length > 0 && (
          
          <Chart
            animation={{ enabled: false }}
            dataSource={motionBarChart}
            rotated={true}
            barGroupPadding={0.2}
          >
            <Size width={'100%'} height={100} />
            <ArgumentAxis categories={['Device Motion']} label={{ visible: false }}>
              <Tick visible={false} />
            </ArgumentAxis>
            <ValueAxis label={{ customizeText: (arg) => moment(arg.value).format('HH:mm') }} />
            <CommonSeriesSettings
              type="rangeBar"
              argumentField="activity"
              rangeValue1Field="start"
              rangeValue2Field="end"
              barOverlapGroup="activity"
            />
            <Legend
              verticalAlignment="center"
              horizontalAlignment="left"
              customizeText={customizeTextLegend}
            />
            <SeriesTemplate nameField="action" customizeSeries={customizeSeries} />
          </Chart>
        )}
      </div>

      <MapContainer
        crs={CRS.Simple}
        bounds={bounds}
        style={{ height: 'calc(100vh - 470px)', width: '100%' }}
      >
        <MapAutoFit bounds={bounds} />
        {floorplan && <ImageOverlay url={floorplan} bounds={bounds} opacity={1} />}
        {normalizedGrid.map(({ x, y, color }, idx) => (
          <Polygon
            key={idx}
            positions={[
              [y, x],
              [y, x + 1],
              [y + 1, x + 1],
              [y + 1, x],
            ]}
            pathOptions={{ fillColor: color, fillOpacity: 0.8, stroke: false }}
          />
        ))}
      </MapContainer>

      <div className="utilizationCheckbox">
        <CheckBox
          value={isMotion}
          style={{ marginBottom: '5px' }}
          text="Motion time"
          onValueChanged={() => onMotionChangeInternal('isMotion')}
        />
        <CheckBox
          value={isStationary}
          text="No motion time"
          onValueChanged={() => onMotionChangeInternal('isStationary')}
        />
      </div>
      {heatmapData.length > 0 && (
        <div
          style={{
            position: 'absolute',
            bottom: '90px',
            right: '20px',
            display: 'flex',
            alignItems: 'center',
            zIndex: 1000,
            backgroundColor: 'white',
            padding: '8px',
            borderRadius: '6px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
            height: 'fit-content',
          }}
        >
          {/* Vertical Label */}
          <div
            style={{
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
              fontWeight: '600',
              fontSize: '12px',
              textAlign: 'center',
              marginRight: '10px',
              lineHeight: '1.2',
              whiteSpace: 'nowrap',
            }}
          >
            Average Utilization per Day
          </div>

          {/* Color Legend Bars */}
          <div
            className="utilization-legend"
            style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
          >
            <div style={legendBoxStyle('#BA8DC1')}>0 - 1 min</div>
            <div style={legendBoxStyle('#99D5FF')}>1 - 5 min</div>
            <div style={legendBoxStyle('#79DE85')}>5 - 15 min</div>
            <div style={legendBoxStyle('#FFFF00')}>15 - 60 min</div>
            <div style={legendBoxStyle('#FEB800')}>1 - 6 hours</div>
            <div style={legendBoxStyle('#FF0000')}>6 - 24 hours</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(UtilizationHeatMap);
